import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Search, SlidersHorizontal, Flame, Clock, ArrowUpDown, X, Heart, Check, Star } from 'lucide-react';
import CATALOG from '../data/catalog';
import { mediaAPI, BACKEND_URL } from '../api';
import { useWishlist } from '../contexts/WishlistContext';
import useEventTracker from '../hooks/useEventTracker';

const SORT_OPTIONS = [
  { id: 'popular', label: 'Popular', icon: Flame },
  { id: 'new', label: 'New', icon: Clock },
  { id: 'low', label: 'Price: Low', icon: ArrowUpDown },
  { id: 'high', label: 'Price: High', icon: ArrowUpDown },
];

const PAGE_SIZE = 20;

const ProductCatalog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gender = 'female', category = 'blouse' } = location.state || {};

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('popular');
  const [styleFilter, setStyleFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [adminMedia, setAdminMedia] = useState([]);
  const loaderRef = useRef(null);
  const { has: isInWishlist, toggle: toggleWishlist } = useWishlist();
  const trackEvent = useEventTracker();

  const genderKey = gender === 'male' ? 'male' : 'female';
  const catData = CATALOG[genderKey]?.[category];
  const categoryName = catData?.name || 'Products';

  // Fetch admin-uploaded media for this category (preferred source)
  useEffect(() => {
    let cancelled = false;
    mediaAPI.publicList(category).then(data => {
      if (!cancelled) setAdminMedia(Array.isArray(data) ? data : []);
    }).catch(() => { if (!cancelled) setAdminMedia([]); });
    return () => { cancelled = true; };
  }, [category]);

  // Merge admin media with procedural catalog:
  // Priority: if admin has ANY uploaded media for this category → show ONLY admin products (no auto-random prices).
  // If no admin media yet → fall back to procedural catalog with its deterministic demo prices.
  const products = useMemo(() => {
    const fallback = catData?.products || [];
    if (adminMedia.length === 0) return fallback;

    return adminMedia.map((m) => {
      // Apply Cloudinary responsive transformation for catalog thumbnails
      const raw = m.url && m.url.startsWith('http') ? m.url : `${BACKEND_URL}${m.url}`;
      const image = raw.includes('/upload/') && raw.includes('res.cloudinary.com')
        ? raw.replace('/upload/', '/upload/c_fill,w_400,h_530,g_auto,q_auto,f_auto/')
        : raw;

      const selling = Number(m.selling_price) || 0;
      const original = Number(m.original_price) || 0;
      const hasPrice = selling > 0;
      const hasMrp = original > selling && selling > 0;
      const discountPct = hasMrp ? Math.round((1 - selling / original) * 100) : 0;

      return {
        id: `adm-${m.id}`,
        name: m.title || 'Premium Design',
        // Admin-controlled prices only (no auto-random). If admin hasn't set price → 0 (UI will show "Price on request")
        price: selling,
        mrp: hasMrp ? original : 0,
        discountPercent: discountPct,
        offerTag: m.offer_tag || '',
        hasPrice,
        style: m.subcategory || 'premium',
        tag: m.featured ? 'bestseller' : m.trending ? 'trending' : m.is_new ? 'new' : '',
        image,
        isAdminUpload: true,
      };
    });
  }, [adminMedia, catData]);

  // Get unique styles from products
  const availableStyles = useMemo(() => {
    const s = new Set(products.map(p => p.style));
    return ['all', ...Array.from(s).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(s) || p.style.toLowerCase().includes(s));
    }
    if (styleFilter !== 'all') {
      list = list.filter(p => p.style === styleFilter);
    }
    switch (sort) {
      case 'low': list.sort((a, b) => a.price - b.price); break;
      case 'high': list.sort((a, b) => b.price - a.price); break;
      case 'new': list.sort((a, b) => (b.tag === 'new' ? 1 : 0) - (a.tag === 'new' ? 1 : 0)); break;
      default: list.sort((a, b) => {
        const order = { bestseller: 4, trending: 3, popular: 2, premium: 1, 'editor-pick': 1, limited: 1 };
        return (order[b.tag] || 0) - (order[a.tag] || 0);
      });
    }
    // ─── POST-SORT DEDUP: spread products with same base image apart so no near-duplicates in feed ───
    const getBaseId = (url) => {
      const m = url && url.match(/photo-([a-f0-9-]+)/);
      return m ? m[1] : url;
    };
    // Group by baseId, then interleave so same image never appears within POOL_SIZE positions
    const groups = new Map();
    for (const p of list) {
      const key = getBaseId(p.image);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    }
    const groupArr = Array.from(groups.values());
    const interleaved = [];
    const maxLen = Math.max(...groupArr.map(g => g.length), 0);
    for (let round = 0; round < maxLen; round++) {
      for (const g of groupArr) {
        if (g[round]) interleaved.push(g[round]);
      }
    }
    return interleaved;
  }, [products, search, sort, styleFilter]);

  // Reset visible count on filter change
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [search, sort, styleFilter]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleCount < filtered.length) {
        setVisibleCount(prev => Math.min(prev + PAGE_SIZE, filtered.length));
      }
    }, { threshold: 0.1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [visibleCount, filtered.length]);

  const visible = filtered.slice(0, visibleCount);

  const handleProductClick = useCallback((product) => {
    // Track view event for recommendations (best-effort)
    trackEvent('view', {
      product_id: product.id,
      category,
      subcategory: product.subcategory || '',
      metadata: { name: product.name, image: product.image, price: product.price },
    });
    navigate('/design-studio', {
      state: { gender, category, categoryName, basePrice: `₹${product.price}`, productId: product.id, productName: product.name, productImage: product.image }
    });
  }, [navigate, gender, category, categoryName, trackEvent]);

  const getTagStyle = (tag) => {
    switch (tag) {
      case 'bestseller': return { bg: '#0F0F0F', color: '#FFFFFF', label: 'Bestseller' };
      case 'new': return { bg: '#D4AF37', color: '#FFFFFF', label: 'New' };
      case 'premium': return { bg: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', color: '#FFFFFF', label: 'Premium' };
      case 'popular': return { bg: 'rgba(212,175,55,0.12)', color: '#D4AF37', label: 'Popular' };
      case 'trending': return { bg: '#991B1B', color: '#FFFFFF', label: 'Trending' };
      case 'editor-pick': return { bg: '#166534', color: '#FFFFFF', label: "Editor's Pick" };
      case 'limited': return { bg: 'rgba(153,27,27,0.1)', color: '#991B1B', label: 'Limited' };
      default: return null;
    }
  };

  const allCats = Object.entries(CATALOG[genderKey] || {}).filter(([k]) => k !== category).slice(0, 5);

  // Product Card
  const ProductCard = React.memo(({ product }) => {
    const tagStyle = getTagStyle(product.tag);
    return (
      <div data-testid={`product-${product.id}`} onClick={() => handleProductClick(product)}
        className="bg-white border text-left cursor-pointer group" style={{ borderColor: '#E5E5E5' }}>
        <div className="relative overflow-hidden aspect-[3/4]">
          {product.image ? (
            <img src={product.image} alt={product.name} loading="lazy" decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          ) : null}
          <div className="w-full h-full items-center justify-center" style={{ display: product.image ? 'none' : 'flex', backgroundColor: '#FAF9F6' }}>
            <div className="text-center px-3">
              <p className="text-[11px] leading-tight" style={{ color: '#D4AF37' }}>{product.name}</p>
            </div>
          </div>
          {tagStyle && (
            <span className="absolute top-1.5 left-1.5 px-1.5 py-px text-[8px] tracking-[0.06em] uppercase"
              style={{ background: tagStyle.bg, color: tagStyle.color }}>{tagStyle.label}</span>
          )}
          <button data-testid={`wishlist-toggle-${product.id}`} onClick={e => {
              e.stopPropagation();
              const wasInWishlist = isInWishlist(product.id);
              toggleWishlist({
                product_id: product.id,
                name: product.name,
                category,
                gender,
                price: product.price || 0,
                mrp: product.mrp || 0,
                image: product.image,
              });
              if (!wasInWishlist) {
                trackEvent('wishlist_add', {
                  product_id: product.id,
                  category,
                  metadata: { name: product.name, image: product.image, price: product.price },
                });
              }
            }}
            className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center bg-white/80" style={{ backdropFilter: 'blur(4px)' }}>
            <Heart className="w-3 h-3" strokeWidth={1.5}
              style={{ color: isInWishlist(product.id) ? '#991B1B' : '#595959', fill: isInWishlist(product.id) ? '#991B1B' : 'transparent' }} />
          </button>
        </div>
        <div className="p-2">
          <p className="text-[11px] leading-tight mb-0.5 line-clamp-1" style={{ color: '#0F0F0F' }}>{product.name}</p>
          {/* Price block — respects admin-uploaded prices first, falls back for procedural demo only */}
          <div className="flex items-baseline gap-1.5 mb-0.5">
            {product.isAdminUpload && !product.hasPrice ? (
              <p className="text-[10px]" style={{ color: '#991B1B' }}>Price on request</p>
            ) : (
              <>
                <p className="text-[12px] font-medium" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{'\u20B9'}{product.price.toLocaleString('en-IN')}</p>
                {product.mrp && product.mrp > product.price && (
                  <>
                    <span className="text-[9px] line-through" style={{ color: '#999' }}>{'\u20B9'}{product.mrp.toLocaleString('en-IN')}</span>
                    <span className="text-[8px] font-medium" style={{ color: '#166534' }}>
                      {product.discountPercent || Math.round((1 - product.price / product.mrp) * 100)}% off
                    </span>
                  </>
                )}
              </>
            )}
          </div>
          {product.offerTag && (
            <span className="inline-block mb-0.5 text-[8px] tracking-[0.05em] uppercase text-white px-1.5 py-0.5" style={{ backgroundColor: '#D4AF37' }}>
              {product.offerTag}
            </span>
          )}
          <div className="flex items-center justify-between">
            {product.rating && (
              <span className="flex items-center gap-0.5 text-[9px] px-1 py-px" style={{ backgroundColor: 'rgba(22,101,52,0.08)', color: '#166534' }}>
                {product.rating} <Star className="w-2 h-2" strokeWidth={2} style={{ fill: '#166534', color: '#166534' }} /> ({product.reviewCount})
              </span>
            )}
            <span className="text-[8px] uppercase px-1 py-px" style={{ backgroundColor: '#FAF9F6', color: '#999' }}>{product.style}</span>
          </div>
        </div>
      </div>
    );
  });

  return (
    <div data-testid="product-catalog" className="min-h-screen pb-4" style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="flex items-center justify-between px-3 py-2.5">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-1 -ml-1">
            <ChevronLeft className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
          </button>
          <div className="text-center">
            <h1 className="text-[14px]" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{categoryName}</h1>
            <p className="text-[9px]" style={{ color: '#999' }}>{filtered.length} designs</p>
          </div>
          <button data-testid="filter-toggle" onClick={() => setShowFilters(!showFilters)} className="p-1">
            <SlidersHorizontal className="w-[18px] h-[18px]" style={{ color: showFilters ? '#D4AF37' : '#595959' }} strokeWidth={1.5} />
          </button>
        </div>
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
            <input data-testid="catalog-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${filtered.length} ${categoryName.toLowerCase()} designs...`}
              className="w-full pl-8 pr-8 py-2 text-[11px] bg-[#F5F3EF] border-0 focus:outline-none" style={{ color: '#0F0F0F' }} />
            {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-3.5 h-3.5" style={{ color: '#BFBFBF' }} strokeWidth={1.5} /></button>}
          </div>
        </div>
        {showFilters && (
          <div className="px-3 pb-2 space-y-1.5 border-t pt-2" style={{ borderColor: '#F0F0F0' }}>
            <div className="flex gap-1 overflow-x-auto scrollbar-hide">
              {SORT_OPTIONS.map(s => (
                <button key={s.id} data-testid={`sort-${s.id}`} onClick={() => setSort(s.id)}
                  className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-[9px] tracking-[0.03em] uppercase whitespace-nowrap"
                  style={{ backgroundColor: sort === s.id ? '#0F0F0F' : '#FFF', color: sort === s.id ? '#FFF' : '#595959', border: `1px solid ${sort === s.id ? '#0F0F0F' : '#E5E5E5'}` }}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1 overflow-x-auto scrollbar-hide">
              {availableStyles.map(s => (
                <button key={s} data-testid={`style-${s}`} onClick={() => setStyleFilter(s)}
                  className="flex-shrink-0 px-2 py-1 text-[9px] uppercase whitespace-nowrap capitalize"
                  style={{ backgroundColor: styleFilter === s ? '#0F0F0F' : '#FFF', color: styleFilter === s ? '#FFF' : '#595959', border: `1px solid ${styleFilter === s ? '#0F0F0F' : '#E5E5E5'}` }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Trust Strip */}
      <div className="px-3 py-1.5 flex items-center justify-center gap-3 text-[8px]" style={{ color: '#999' }}>
        <span className="flex items-center gap-0.5"><Check className="w-2.5 h-2.5" style={{ color: '#166534' }} strokeWidth={2} />Free Alterations</span>
        <span className="flex items-center gap-0.5"><Check className="w-2.5 h-2.5" style={{ color: '#166534' }} strokeWidth={2} />7-Day Returns</span>
        <span className="flex items-center gap-0.5"><Check className="w-2.5 h-2.5" style={{ color: '#166534' }} strokeWidth={2} />Verified Tailors</span>
      </div>

      {/* Quick Sort + Count */}
      <div className="px-3 py-1 flex items-center justify-between">
        <p className="text-[10px]" style={{ color: '#999' }}>Showing {visible.length} of {filtered.length}</p>
        <button data-testid="sort-quick" onClick={() => setSort(sort === 'low' ? 'high' : 'low')}
          className="text-[9px] flex items-center gap-0.5" style={{ color: '#D4AF37' }}>
          <ArrowUpDown className="w-3 h-3" strokeWidth={1.5} /> Price
        </button>
      </div>

      {/* Product Grid */}
      <div className="px-2">
        {visible.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-1.5">
              {visible.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
            {/* Infinite Scroll Loader */}
            {visibleCount < filtered.length && (
              <div ref={loaderRef} className="py-6 text-center">
                <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
                <p className="text-[10px] mt-2" style={{ color: '#999' }}>Loading more designs...</p>
              </div>
            )}
            {visibleCount >= filtered.length && filtered.length > PAGE_SIZE && (
              <p className="text-[10px] text-center py-4" style={{ color: '#BFBFBF' }}>You've seen all {filtered.length} designs</p>
            )}
          </>
        ) : (
          <div className="py-16 text-center">
            <Search className="w-8 h-8 mx-auto mb-2" style={{ color: '#E5E5E5' }} strokeWidth={1.5} />
            <p className="text-[12px]" style={{ color: '#595959' }}>No designs found</p>
            <button onClick={() => { setSearch(''); setStyleFilter('all'); }} className="mt-2 text-[11px]" style={{ color: '#D4AF37' }}>Clear filters</button>
          </div>
        )}
      </div>

      {/* Related Categories */}
      {allCats.length > 0 && (
        <div className="px-3 mt-4">
          <p className="text-[9px] tracking-[0.12em] uppercase mb-2" style={{ color: '#999' }}>You may also like</p>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-2">
            {allCats.map(([catId, catInfo]) => (
              <button key={catId} data-testid={`related-${catId}`}
                onClick={() => { navigate('/product-catalog', { state: { gender, category: catId } }); setVisibleCount(PAGE_SIZE); setSearch(''); setStyleFilter('all'); }}
                className="flex-shrink-0 bg-white border px-3 py-2 text-center min-w-[90px]" style={{ borderColor: '#E5E5E5' }}>
                <p className="text-[11px] font-medium" style={{ color: '#0F0F0F' }}>{catInfo.name}</p>
                <p className="text-[9px]" style={{ color: '#999' }}>{catInfo.products.length}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar{display:none}.scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}
        .line-clamp-1{display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden}
      `}</style>
    </div>
  );
};

export default ProductCatalog;
